import type { DirectorRequestDetail } from '@/types/director'
import { ABSENCE_TYPE_LABELS, GATE_TRIGGER_LABELS, PAYMENT_TYPE_LABELS } from '@/types/director'
import { formatDateRange, daysCount } from '@/utils/caseList'

interface CaseFactsGridProps {
  detail: DirectorRequestDetail
}

/** Fallakte v2 (P5.4): Fakten-Karten (Abwesenheit, Lektionen, Auslöser, Grund). */
export function CaseFactsGrid({ detail }: CaseFactsGridProps) {
  const triggers = ((detail.gate_reason as { triggers?: string[] } | null)?.triggers) ?? []
  const absenceLabel = detail.absence_type_label
    || (detail.absence_type ? ABSENCE_TYPE_LABELS[detail.absence_type] ?? detail.absence_type : null)
  const days = daysCount(detail.date, detail.date_to)
  // K6: wirksame Lektionen vs. Zeilen können abweichen (entfernte/stornierte)
  const rows = detail.lessons.length
  const countMismatch = detail.lesson_count !== rows

  const payer = detail.latest_decision?.decision_type === 'approved'
    ? detail.latest_decision.payment_type
    : null

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Abwesenheit</div>
        <div className="text-sm font-semibold">{absenceLabel || '—'}</div>
        <div className="text-[11px] text-gray-400 mt-0.5">{formatDateRange(detail.date, detail.date_to)} · {days} {days === 1 ? 'Tag' : 'Tage'}</div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Lektionen</div>
        <div className="text-sm font-semibold">{detail.lesson_count}</div>
        {countMismatch && (
          <div className="text-[11px] text-amber-600 mt-0.5" title="Wirksame Lektionen vs. angezeigte Zeilen">
            {detail.lesson_count} wirksam · {rows} Zeilen
          </div>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Ausgelöst durch</div>
        <div className="text-sm font-semibold">
          {triggers.length > 0
            ? triggers.map(t => GATE_TRIGGER_LABELS[t] || t).join(', ')
            : 'Zahler-Entscheidung'}
        </div>
        {detail.account_hold && (
          <div className="text-[11px] text-red-600 font-medium mt-0.5">Konto überschritten</div>
        )}
      </div>
      <div
        className="bg-white border border-gray-200 rounded-lg p-3 min-w-0"
        title={detail.reason || undefined}
      >
        <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Grund (Lehrer)</div>
        <div className="text-sm font-semibold whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {detail.reason || '—'}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="case-payer">
        <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Zahler</div>
        <div className="text-sm font-semibold">
          {payer ? (PAYMENT_TYPE_LABELS[payer] || payer) : '—'}
        </div>
      </div>
    </div>
  )
}
